import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Artist } from './types';
import { artistsApi } from '../../repositories/artists';
import PhotoUpload from './PhotoUpload';
import Swal from 'sweetalert2';

interface ArtistDetailsTabProps {
  artist: Artist;
  onUpdate: (updatedArtist: Artist) => void;
}

// Types
interface UpdateArtistFormValues {
  artistName: string;
  spotifyURI: string;
  appleID: string;
  asin: string;
  soundcloudURL: string;
  tidalURL: string;
  bio: string;
  hometown: string;
  currentTown: string;
  country: string;
  label: string;
  distributor: string;
  instagram: string;
  tiktok: string;
  twitter: string;
  facebook: string;
  snapchat: string;
  spotifyURL: string;
  appleURL: string;
  amazonURL: string;
  deezerURL: string;
}

// Validation Schema - same as AddArtist but all fields optional for updates
const updateArtistValidationSchema = Yup.object().shape({
  // Required fields - but optional for updates
  artistName: Yup.string()
    .min(2, 'Artist name must be at least 2 characters')
    .max(255, 'Artist name must be less than 255 characters'),

  spotifyURI: Yup.string()
    .matches(
      /^[a-zA-Z0-9]{22}$/,
      'Invalid Spotify URI format. Should be 22 characters'
    ),

  appleID: Yup.string()
    .matches(/^\d+$/, 'Apple ID must contain only numbers'),

  asin: Yup.string(),

  soundcloudURL: Yup.string()
    .url('Invalid SoundCloud URL format'),

  tidalURL: Yup.string()
    .url('Invalid TIDAL URL format'),

  // Optional fields
  bio: Yup.string()
    .max(5000, 'Bio must be less than 5000 characters'),

  hometown: Yup.string()
    .max(100, 'Hometown must be less than 100 characters'),

  currentTown: Yup.string()
    .max(100, 'Current town must be less than 100 characters'),

  country: Yup.string()
    .max(100, 'Country must be less than 100 characters'),

  label: Yup.string()
    .max(100, 'Label must be less than 100 characters'),

  distributor: Yup.string()
    .max(100, 'Distributor must be less than 100 characters'),

  // Social Media URLs
  instagram: Yup.string()
    .url('Invalid Instagram URL format')
    .nullable(),

  tiktok: Yup.string()
    .url('Invalid TikTok URL format')
    .nullable(),

  twitter: Yup.string()
    .url('Invalid Twitter URL format')
    .nullable(),

  facebook: Yup.string()
    .url('Invalid Facebook URL format')
    .nullable(),

  snapchat: Yup.string()
    .url('Invalid Snapchat URL format')
    .nullable(),

  // Streaming platform URLs
  spotifyURL: Yup.string()
    .url('Invalid Spotify URL format')
    .nullable(),

  appleURL: Yup.string()
    .url('Invalid Apple Music URL format')
    .nullable(),

  amazonURL: Yup.string()
    .url('Invalid Amazon URL format')
    .nullable(),

  deezerURL: Yup.string()
    .url('Invalid Deezer URL format')
    .nullable(),
});

const ArtistDetailsTab: React.FC<ArtistDetailsTabProps> = ({ artist, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>(artist.photo || '');

  // Initial form values from artist data
  const initialValues: UpdateArtistFormValues = {
    artistName: artist.artistName || '',
    spotifyURI: artist.spotifyURI || '',
    appleID: artist.appleID || '',
    asin: artist.asin || '',
    soundcloudURL: artist.soundcloudURL || '',
    tidalURL: artist.tidalURL || '',
    bio: artist.bio || '',
    hometown: artist.hometown || '',
    currentTown: artist.currentTown || '',
    country: artist.country || '',
    label: artist.label || '',
    distributor: artist.distributor || '',
    instagram: artist.instagram || '',
    tiktok: artist.tiktok || '',
    twitter: artist.twitter || '',
    facebook: artist.facebook || '',
    snapchat: artist.snapchat || '',
    spotifyURL: artist.spotifyURL || '',
    appleURL: artist.appleURL || '',
    amazonURL: artist.amazonURL || '',
    deezerURL: artist.deezerURL || ''
  };

  const handlePhotoSelect = (file: File) => {
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (values: UpdateArtistFormValues) => {
    try {
      setLoading(true);
      
      // Prepare update data with backend field names
      const updateData = new FormData();
      
      // Add photo if selected
      if (photoFile) {
        updateData.append('photo', photoFile);
      }

      // Required fields - use backend field names
      updateData.append('artistName', values.artistName.trim());
      updateData.append('spotifyURI', values.spotifyURI.trim());
      updateData.append('appleID', values.appleID.trim());
      updateData.append('asin', values.asin.trim());
      updateData.append('soundcloudURL', values.soundcloudURL.trim());
      updateData.append('tidalURL', values.tidalURL.trim());
      
      // Optional fields - only append if they have values
      if (values.bio.trim()) updateData.append('bio', values.bio.trim());
      if (values.hometown.trim()) updateData.append('hometown', values.hometown.trim());
      if (values.currentTown.trim()) updateData.append('currentTown', values.currentTown.trim());
      if (values.country.trim()) updateData.append('country', values.country.trim());
      if (values.label.trim()) updateData.append('label', values.label.trim());
      if (values.distributor.trim()) updateData.append('distributor', values.distributor.trim());
      if (values.instagram.trim()) updateData.append('instagram', values.instagram.trim());
      if (values.tiktok.trim()) updateData.append('tiktok', values.tiktok.trim());
      if (values.twitter.trim()) updateData.append('twitter', values.twitter.trim());
      if (values.facebook.trim()) updateData.append('facebook', values.facebook.trim());
      if (values.snapchat.trim()) updateData.append('snapchat', values.snapchat.trim());
      if (values.spotifyURL.trim()) updateData.append('spotifyURL', values.spotifyURL.trim());
      if (values.appleURL.trim()) updateData.append('appleURL', values.appleURL.trim());
      if (values.amazonURL.trim()) updateData.append('amazonURL', values.amazonURL.trim());
      if (values.deezerURL.trim()) updateData.append('deezerURL', values.deezerURL.trim());

      const response = await artistsApi.update(artist.id, updateData);
      const updatedArtistData = response.data.data || response.data;

      // Create updated artist with backend field names
      const mappedArtist: Artist = {
        ...artist,
        id: updatedArtistData.id,
        artistName: updatedArtistData.artistName,
        photo: updatedArtistData.photo,
        spotifyURI: updatedArtistData.spotifyURI,
        appleID: updatedArtistData.appleID,
        asin: updatedArtistData.asin,
        soundcloudURL: updatedArtistData.soundcloudURL,
        tidalURL: updatedArtistData.tidalURL,
        bio: updatedArtistData.bio,
        hometown: updatedArtistData.hometown,
        currentTown: updatedArtistData.currentTown,
        country: updatedArtistData.country,
        label: updatedArtistData.label,
        distributor: updatedArtistData.distributor,
        instagram: updatedArtistData.instagram,
        tiktok: updatedArtistData.tiktok,
        twitter: updatedArtistData.twitter,
        facebook: updatedArtistData.facebook,
        snapchat: updatedArtistData.snapchat,
        spotifyURL: updatedArtistData.spotifyURL,
        appleURL: updatedArtistData.appleURL,
        amazonURL: updatedArtistData.amazonURL,
        deezerURL: updatedArtistData.deezerURL,
        userId: updatedArtistData.userId || artist.userId,
        pitchCount: updatedArtistData.pitchCount || artist.pitchCount,
        createdAt: new Date(updatedArtistData.createdAt),
        updatedAt: new Date(updatedArtistData.updatedAt),
      };

      onUpdate(mappedArtist);
      setIsEditing(false);
      setPhotoFile(null);
      
      Swal.fire({
        title: 'Success!',
        text: 'Artist details updated successfully.',
        icon: 'success',
        confirmButtonText: 'OK'
      });
      
    } catch (error: any) {
      console.error('Error updating artist:', error);

      if (error.response?.status === 400 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        
        let errorMessage = "Validation failed:\n";
        validationErrors.forEach((err: any, index: number) => {
          errorMessage += `• ${err}\n`;
        });
        
        Swal.fire({
          title: 'Validation Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } else {
        // Handle other errors
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            error.message ||
                            'Failed to update artist details. Please try again.';
        
        Swal.fire({
          title: 'Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPhotoPreview(artist.photo || '');
    setPhotoFile(null);
    setIsEditing(false);
  };

  // Custom input component with error handling - EXACT same as AddArtist
  const InputField = ({ 
    name, 
    label, 
    placeholder, 
    type = 'text', 
    required = false,
    maxLength,
    rows
  }: {
    name: string;
    label: string;
    placeholder: string;
    type?: string;
    required?: boolean;
    maxLength?: number;
    rows?: number;
  }) => (
    <div className="space-y-2">
      <label htmlFor={name} className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <Field name={name}>
        {({ field, form, meta }: any) => (
          <>
            {type === 'textarea' ? (
              <textarea
                {...field}
                id={name}
                placeholder={placeholder}
                maxLength={maxLength}
                rows={rows}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  meta.touched && meta.error ? 'border-red-500' : 'border-gray-300'
                }`}
                onChange={(e) => {
                  // Special handling for Apple ID - only numbers
                  if (name === 'appleID') {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    form.setFieldValue(name, numericValue);
                    return;
                  }
                  
                  // Special handling for Spotify URI - clean format
                  if (name === 'spotifyURI') {
                    let cleanValue = e.target.value;
                    if (cleanValue.includes('spotify:artist:')) {
                      cleanValue = cleanValue.replace('spotify:artist:', '');
                    }
                    form.setFieldValue(name, cleanValue);
                    return;
                  }
                  
                  field.onChange(e);
                }}
              />
            ) : (
              <input
                {...field}
                type={type}
                id={name}
                placeholder={placeholder}
                maxLength={maxLength}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  meta.touched && meta.error ? 'border-red-500' : 'border-gray-300'
                }`}
                onChange={(e) => {
                  // Special handling for Apple ID - only numbers
                  if (name === 'appleID') {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    form.setFieldValue(name, numericValue);
                    return;
                  }
                  
                  // Special handling for Spotify URI - clean format
                  if (name === 'spotifyURI') {
                    let cleanValue = e.target.value;
                    if (cleanValue.includes('spotify:artist:')) {
                      cleanValue = cleanValue.replace('spotify:artist:', '');
                    }
                    form.setFieldValue(name, cleanValue);
                    return;
                  }
                  
                  field.onChange(e);
                }}
              />
            )}
            {/* Character counter for bio */}
            {name === 'bio' && (
              <div className="text-sm text-gray-500">
                {field.value.length}/5000 characters
              </div>
            )}
            <ErrorMessage name={name} component="div" className="text-sm text-red-500" />
          </>
        )}
      </Field>
    </div>
  );

  if (!isEditing) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Artist Photo and Name */}
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-4 overflow-hidden bg-gray-200 rounded-lg">
                {artist.photo ? (
                  <img
                    src={artist.photo}
                    alt={artist.artistName}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full">
                    <svg
                      className="w-16 h-16 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900">{artist.artistName}</h2>
            </div>

            {/* Bio */}
            {artist.bio && (
              <div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">Bio</h3>
                <p className="text-gray-700">{artist.bio}</p>
              </div>
            )}

            {/* Platform IDs */}
            <div>
              <h3 className="mb-2 text-lg font-medium text-gray-900">Platform IDs</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Spotify URI:</span> {artist.spotifyURI}
                </div>
                <div>
                  <span className="font-medium">Apple ID:</span> {artist.appleID}
                </div>
                <div>
                  <span className="font-medium">ASIN:</span> {artist.asin}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Social Media */}
            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">Social Media</h3>
              <div className="space-y-1 text-sm text-gray-600">
                {artist.instagram && <div><span className="font-medium">Instagram:</span> {artist.instagram}</div>}
                {artist.tiktok && <div><span className="font-medium">TikTok:</span> {artist.tiktok}</div>}
                {artist.twitter && <div><span className="font-medium">X:</span> {artist.twitter}</div>}
                {artist.facebook && <div><span className="font-medium">Facebook:</span> {artist.facebook}</div>}
                {artist.snapchat && <div><span className="font-medium">Snapchat:</span> {artist.snapchat}</div>}
                {!artist.instagram && !artist.tiktok && !artist.twitter && !artist.facebook && !artist.snapchat && (
                  <div className="text-gray-400">No social media links provided</div>
                )}
              </div>
            </div>

            {/* Location */}
            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">Location</h3>
              <div className="text-sm text-gray-600">
                {(artist.hometown || artist.currentTown) ? (
                  <div>
                    {artist.hometown && <><span className="font-medium">Hometown:</span> {artist.hometown}</>}
                    {artist.hometown && artist.currentTown && ' | '}
                    {artist.currentTown && <><span className="font-medium">Current:</span> {artist.currentTown}</>}
                    {artist.country && <div><span className="font-medium">Country:</span> {artist.country}</div>}
                  </div>
                ) : (
                  <div className="text-gray-400">No location information provided</div>
                )}
              </div>
            </div>

            {/* Business */}
            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">Business</h3>
              <div className="text-sm text-gray-600">
                {(artist.label || artist.distributor) ? (
                  <div>
                    {artist.label && <><span className="font-medium">Label:</span> {artist.label}</>}
                    {artist.label && artist.distributor && ' | '}
                    {artist.distributor && <><span className="font-medium">Distributor:</span> {artist.distributor}</>}
                  </div>
                ) : (
                  <div className="text-gray-400">No business information provided</div>
                )}
              </div>
            </div>

            {/* Streaming URLs */}
            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">Streaming URLs</h3>
              <div className="space-y-2 text-sm">
                {artist.soundcloudURL && (
                  <div><span className="font-medium">SoundCloud:</span> 
                    <a href={artist.soundcloudURL} target="_blank" rel="noopener noreferrer" className="ml-1 text-indigo-600 hover:text-indigo-500">
                      {artist.soundcloudURL}
                    </a>
                  </div>
                )}
                {artist.tidalURL && (
                  <div><span className="font-medium">TIDAL:</span> 
                    <a href={artist.tidalURL} target="_blank" rel="noopener noreferrer" className="ml-1 text-indigo-600 hover:text-indigo-500">
                      {artist.tidalURL}
                    </a>
                  </div>
                )}
                {artist.spotifyURL && (
                  <div><span className="font-medium">Spotify:</span> 
                    <a href={artist.spotifyURL} target="_blank" rel="noopener noreferrer" className="ml-1 text-indigo-600 hover:text-indigo-500">
                      {artist.spotifyURL}
                    </a>
                  </div>
                )}
                {artist.appleURL && (
                  <div><span className="font-medium">Apple:</span> 
                    <a href={artist.appleURL} target="_blank" rel="noopener noreferrer" className="ml-1 text-indigo-600 hover:text-indigo-500">
                      {artist.appleURL}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Edit
          </button>
        </div>
      </div>
    );
  }

  // Edit Mode with EXACT same UI layout as AddArtist
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Edit Artist</h1>
          <button
            onClick={handleCancel}
            className="text-gray-600 hover:text-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={updateArtistValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched }) => (
          <Form className="p-6 space-y-6">
            {/* Photo Upload */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Artist Photo
              </label>
              <PhotoUpload
                onPhotoSelect={handlePhotoSelect}
                preview={photoPreview}
              />
            </div>

            {/* Required Fields */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Required Information</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <InputField
                  name="artistName"
                  label="Artist Name"
                  placeholder="Enter Artist Name"
                  required
                />

                <InputField
                  name="spotifyURI"
                  label="Spotify URI"
                  placeholder="25BObiRSDCMwVrBGIVaLIf"
                  required
                />

                <InputField
                  name="appleID"
                  label="Apple ID"
                  placeholder="123456789"
                  required
                />

                <InputField
                  name="asin"
                  label="ASIN"
                  placeholder="B07XVQZXYZ"
                  required
                />

                <InputField
                  name="soundcloudURL"
                  label="SoundCloud URL"
                  placeholder="https://soundcloud.com/artist-name"
                  type="url"
                  required
                />

                <InputField
                  name="tidalURL"
                  label="TIDAL URL"
                  placeholder="https://tidal.com/artist/artist-name"
                  type="url"
                  required
                />
              </div>
            </div>

            {/* Optional Fields */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Optional Information</h3>
              
              <InputField
                name="bio"
                label="Bio"
                placeholder="Enter artist bio..."
                type="textarea"
                maxLength={5000}
                rows={4}
              />

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <InputField
                  name="hometown"
                  label="Artist Hometown"
                  placeholder="Enter hometown"
                  maxLength={100}
                />

                <InputField
                  name="currentTown"
                  label="Current Town"
                  placeholder="Enter current town"
                  maxLength={100}
                />

                <InputField
                  name="country"
                  label="Country"
                  placeholder="Enter country"
                  maxLength={100}
                />

                <InputField
                  name="label"
                  label="Label"
                  placeholder="Enter label"
                  maxLength={100}
                />

                <InputField
                  name="distributor"
                  label="Distributor"
                  placeholder="Enter distributor"
                  maxLength={100}
                />
              </div>

              {/* Social Media */}
              <div>
                <h4 className="mb-3 font-medium text-gray-900 text-md">Social Media</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  <InputField
                    name="instagram"
                    label="Instagram"
                    placeholder="https://instagram.com/username"
                    type="url"
                  />

                  <InputField
                    name="tiktok"
                    label="TikTok"
                    placeholder="https://tiktok.com/@username"
                    type="url"
                  />

                  <InputField
                    name="twitter"
                    label="X"
                    placeholder="https://x.com/username"
                    type="url"
                  />

                  <InputField
                    name="facebook"
                    label="Facebook"
                    placeholder="https://facebook.com/username"
                    type="url"
                  />

                  <InputField
                    name="snapchat"
                    label="Snapchat"
                    placeholder="https://snapchat.com/add/username"
                    type="url"
                  />
                </div>
              </div>

              {/* Streaming URLs */}
              <div>
                <h4 className="mb-3 font-medium text-gray-900 text-md">Streaming URLs</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  <InputField
                    name="spotifyURL"
                    label="Spotify URL"
                    placeholder="https://open.spotify.com/artist/..."
                    type="url"
                  />

                  <InputField
                    name="appleURL"
                    label="Apple URL"
                    placeholder="https://music.apple.com/artist/..."
                    type="url"
                  />

                  <InputField
                    name="amazonURL"
                    label="Amazon URL"
                    placeholder="https://amazon.com/artist/..."
                    type="url"
                  />

                  <InputField
                    name="deezerURL"
                    label="Deezer URL"
                    placeholder="https://deezer.com/artist/..."
                    type="url"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                onClick={() => {
                  // Auto-scroll to first error field like in AddArtist
                  setTimeout(() => {
                    const errorElement = document.querySelector(
                      '.text-red-500, .border-red-500'
                    );
                    if (errorElement) {
                      errorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                      });
                    }
                  }, 200);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <span className="inline-block w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ArtistDetailsTab;